import React, { useMemo } from 'react';
import { Filter, OperatorType, RadioButton } from '@fabric-ui/components';
import { Box, Divider } from '@mui/material';
import {
  FieldFilterMode,
  getFieldTypeInfo,
  isAdvancedFilterMode,
  isConditionFilterMode,
  isListFilterMode,
} from '../../../utils/filterUtils';
import { useFilterConfig } from '../FilterContext';
import FilterActionButtons from './FilterActionButtons';
import FilterByAdvanced from './FilterByAdvanced';
import { FilterByCondition } from './FilterByCondition';
import { FilterByList } from './FilterByList';

interface FieldFilterTabsProps {
  field: string;
  type: string;
  mode: string;
  setMode: (mode: string) => void;
  isSchemaField: boolean;
  initialFilter: any;
  label: React.ReactNode;
  // List props
  isLoadingOptions: boolean;
  calcOptions: any[];
  value: any;
  totalRowCount: number | undefined;
  setSearch: (search: string) => void;
  onChange: (field: string, value: any) => void;
  onFilterChange?: (filter: Filter) => void;
  filterObject: any;
  // Condition props
  conditionValue: any;
  operator: OperatorType;
  isMultipleCondition: boolean;
  disablePortalInOperatorSelect: boolean;
  handleSelectConditionChange: (value: unknown, index: number) => void;
  setErrors: (errors: any) => void;
  setConditionValue: (value: any) => void;
  errors: any;
  // Advanced props
  activeProjName: string | undefined;
  advancedFilterValue: any;
  handleAdvancedFilterChange: (value: any) => void;
  // Action buttons props
  setOpen: (open: boolean) => void;
  getErrors: (val: any, operator: any) => any;
  setAdvancedFilterValue: (value: any) => void;
  open?: boolean;
}

export const FieldFilterTabs: React.FC<FieldFilterTabsProps> = ({
  field,
  type,
  mode,
  setMode,
  isSchemaField,
  initialFilter,
  label,
  isLoadingOptions,
  calcOptions,
  value,
  totalRowCount,
  setSearch,
  onChange,
  onFilterChange,
  filterObject,
  conditionValue,
  operator,
  isMultipleCondition,
  disablePortalInOperatorSelect,
  handleSelectConditionChange,
  setErrors,
  setConditionValue,
  errors,
  activeProjName,
  advancedFilterValue,
  handleAdvancedFilterChange,
  setOpen,
  getErrors,
  setAdvancedFilterValue,
  open = false,
}) => {
  const { showAdvanced } = useFilterConfig();

  const viewOptions = useMemo<Array<{ title: string; value: string }>>(() => {
    const opts: Array<{ title: string; value: string }> = [
      { title: 'List', value: FieldFilterMode.LIST },
      { title: 'Condition', value: FieldFilterMode.CONDITION },
    ];
    if (showAdvanced) {
      opts.push({ title: 'Advanced', value: FieldFilterMode.ADVANCED });
    }
    return opts;
  }, [showAdvanced]);

  const { isNumberField, isBooleanField, isTextField } = getFieldTypeInfo(type);

  return (
    <>
      {initialFilter && !isNumberField && (
        <Box className="px-4 py-2">
          <RadioButton options={viewOptions} value={mode} onChange={setMode} />
        </Box>
      )}
      <Divider />

      {(isTextField || isBooleanField) && isListFilterMode(mode) && (
        <FilterByList
          label={label}
          isLoadingOptions={isLoadingOptions}
          options={calcOptions}
          value={filterObject ? [] : isBooleanField ? value?.map(JSON.parse) : value}
          onChange={onChange}
          field={field}
          type={type}
          onFilterChange={onFilterChange}
          totalOptionsCount={totalRowCount ?? 0}
          isSchemaField={isSchemaField}
          setSearch={setSearch}
          open={open}
        />
      )}

      {(isConditionFilterMode(mode) || !isTextField) && (
        <FilterByCondition
          initialFilter={initialFilter}
          conditionValue={conditionValue}
          operator={operator}
          isMultipleCondition={isMultipleCondition}
          disablePortalInOperatorSelect={disablePortalInOperatorSelect}
          type={type}
          handleSelectConditionChange={handleSelectConditionChange}
          setErrors={setErrors}
          setConditionValue={setConditionValue}
          onChange={onChange}
          field={field}
          onFilterChange={onFilterChange}
          errors={errors}
        />
      )}

      {isAdvancedFilterMode(mode) && (
        <FilterByAdvanced activeProjName={activeProjName} advancedFilter={advancedFilterValue} onChange={handleAdvancedFilterChange} />
      )}

      <Divider orientation="horizontal" />

      {(isConditionFilterMode(mode) || isAdvancedFilterMode(mode) || isNumberField) && (
        <FilterActionButtons
          setErrors={setErrors}
          setConditionValue={setConditionValue}
          operator={operator}
          initialFilter={initialFilter}
          setOpen={setOpen}
          onChange={onChange}
          field={field}
          onFilterChange={onFilterChange}
          conditionValue={conditionValue}
          getErrors={getErrors}
          mode={mode}
          advancedFilterValue={advancedFilterValue}
          setAdvancedFilterValue={setAdvancedFilterValue}
        />
      )}
    </>
  );
};
